/**
 * Audit-record export — Blob-download glue for the Mandates view.
 *
 * Two helpers, both delegate to the audit bag:
 *   - exportAuditRecordJSON → GET /v1/audit/:id/bundle (signed JSON).
 *   - exportAuditBundleHTML → GET /v1/audit/:id/bundle?format=html
 *     (server-rendered HTML with the canonical signed JSON embedded
 *     in a <script type="application/sill+json"> block per ADR-0024).
 *
 * Both endpoints return the DAY bundle — every record for the site on
 * the record's chain and UTC day — not a single-record artifact, so both
 * downloads use the shared day-bundle name from `@sill/audit`:
 *   `sill-audit-{domain}-{YYYY-MM-DD}-{chain}.{json|html}`
 *
 * The http client hands back only the Blob (and the API's CORS layer
 * doesn't expose Content-Disposition), so the name is computed here from
 * the record with the same helper the API uses for its header.
 *
 * Both return a Blob that this helper turns into a browser download
 * via an object URL + click + revoke cycle.
 *
 * ADR-0227 — a day with more records than fit in one bundle exports as
 * parts. The helpers ask the API how the day splits first; on a split
 * day they download the part holding the record (named
 * `…-part-{k}-of-{n}.{ext}`) and return a `BundleSplit` so the view can
 * offer the other parts (`BundlePartsNote`).
 */

import { auditBundleFilename, decisionClassOf } from '@sill/audit';
import type { AuditRecord } from '@sill/types';
import type {
  AuditBundlePartsManifest,
  AuditBundleScope,
} from '@sill/types/api';

import type { SillApi } from '../../api/contract.js';
import { ApiError } from '../../api/http-client.js';

function download(blob: Blob, filename: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** UTC `YYYY-MM-DD` of the record's `evaluated_at` — the same day bucket
 *  the API computes (`(evaluated_at AT TIME ZONE 'UTC')::date`). */
export function utcDayOf(evaluated_at: string): string {
  const ms = Date.parse(evaluated_at);
  return Number.isNaN(ms)
    ? evaluated_at.slice(0, 10)
    : new Date(ms).toISOString().slice(0, 10);
}

/** Download filename for the day bundle `record` belongs to. */
export function auditBundleFilenameFor(
  record: AuditRecord,
  siteDomain: string | undefined,
  ext: 'json' | 'html',
): string {
  return auditBundleFilename({
    domain: siteDomain,
    utc_date: utcDayOf(record.evaluated_at),
    chain: decisionClassOf(record.decision),
    ext,
    record_id: record.record_id,
  });
}

/**
 * ADR-0227 — what the merchant needs to fetch the rest of a split day:
 * returned by the export helpers when the record's day is too large for
 * one bundle (null when the whole day came down in one file).
 */
export interface BundleSplit {
  readonly ext: 'json' | 'html';
  /** The part just downloaded — always the one holding the record. */
  readonly downloaded_part: number;
  readonly manifest: AuditBundlePartsManifest;
}

/** Ask the API how the record's day splits. Null when the manifest is
 *  unavailable (e.g. an API that predates parts) — the plain download
 *  still works: the server then picks the record's part itself. */
async function fetchManifest(
  api: SillApi,
  record: AuditRecord,
): Promise<AuditBundlePartsManifest | null> {
  try {
    const m = await api.audit.bundleParts(record.record_id);
    return Number.isSafeInteger(m.part_count) && m.part_count >= 1 ? m : null;
  } catch {
    return null;
  }
}

function filenameFromManifest(
  m: AuditBundlePartsManifest,
  siteDomain: string | undefined,
  ext: 'json' | 'html',
  part: number,
  record_id: string,
): string {
  return auditBundleFilename({
    domain: siteDomain,
    utc_date: m.utc_date,
    chain: m.decision_class === 'transactional' ? 'transactional' : 'discovery',
    ext,
    part: { index: part, count: m.part_count },
    record_id,
  });
}

async function exportBundle(
  api: SillApi,
  record: AuditRecord,
  siteDomain: string | undefined,
  ext: 'json' | 'html',
): Promise<BundleSplit | null> {
  const fetchBlob = (part?: number): Promise<Blob> => {
    const opts = part !== undefined ? { part } : undefined;
    return ext === 'json'
      ? api.audit.exportRecordJson(record.record_id, opts)
      : api.audit.exportBundleHtml(record.record_id, opts);
  };
  const manifest = await fetchManifest(api, record);
  if (manifest === null || manifest.part_count <= 1) {
    const blob = await fetchBlob();
    download(
      blob,
      manifest !== null
        ? filenameFromManifest(manifest, siteDomain, ext, 1, record.record_id)
        : auditBundleFilenameFor(record, siteDomain, ext),
    );
    return null;
  }
  // Split day: download the part that holds the clicked record, so the
  // record the merchant opened is always in the file they get.
  const part = manifest.record_part;
  const blob = await fetchBlob(part);
  download(blob, filenameFromManifest(manifest, siteDomain, ext, part, record.record_id));
  return { ext, downloaded_part: part, manifest };
}

export async function exportAuditRecordJSON(
  api: SillApi,
  record: AuditRecord,
  siteDomain?: string,
): Promise<BundleSplit | null> {
  return await exportBundle(api, record, siteDomain, 'json');
}

export async function exportAuditBundleHTML(
  api: SillApi,
  record: AuditRecord,
  siteDomain?: string,
): Promise<BundleSplit | null> {
  return await exportBundle(api, record, siteDomain, 'html');
}

/** Download one specific part of a split day (ADR-0227). */
export async function exportAuditBundlePart(
  api: SillApi,
  record: AuditRecord,
  split: BundleSplit,
  part: number,
  siteDomain?: string,
): Promise<void> {
  const blob = split.ext === 'json'
    ? await api.audit.exportRecordJson(record.record_id, { part })
    : await api.audit.exportBundleHtml(record.record_id, { part });
  download(blob, filenameFromManifest(split.manifest, siteDomain, split.ext, part, record.record_id));
}

/**
 * Legal-review follow-up (2026-10-25) — narrowed export: either every
 * attack-match record or every record from the same caller, over a UTC
 * date range of up to 31 days. The dashboard enforces the 31-day limit
 * before calling; the server fails closed on the same bound AND caps
 * the result at 10,000 records (422 `scope_too_many_records`).
 *
 * The server returns the signed body. Each record keeps its own ed25519
 * signature; the whole file is signed as a unit; the signed body
 * carries the scope under its own domain separator and no `batch_roots`
 * (a filtered subset is not covered by any day root). All signatures
 * verify at sill.so/verify; the day-root check is reported there as
 * not applicable, never as passed.
 */
export interface NarrowedExportRequest {
  readonly scope: AuditBundleScope;
  readonly from: string;
  readonly to: string;
  readonly ext: 'json' | 'html';
}

export async function exportAuditNarrowedBundle(
  api: SillApi,
  record: AuditRecord,
  req: NarrowedExportRequest,
  siteDomain?: string,
): Promise<void> {
  const opts = { scope: req.scope, from: req.from, to: req.to };
  const blob = req.ext === 'json'
    ? await api.audit.exportRecordJson(record.record_id, opts)
    : await api.audit.exportBundleHtml(record.record_id, opts);
  const name = auditBundleFilename({
    domain: siteDomain,
    utc_date: req.from,
    chain: decisionClassOf(record.decision),
    ext: req.ext,
    record_id: record.record_id,
    narrowed: req.scope,
  });
  download(blob, name);
}

/** Plain-words message for the narrowed-export 422/429 responses so
 *  the dashboard asks the merchant to narrow the range or wait. */
export function narrowedExportErrorMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status === 422 && err.server_reason === 'scope_too_many_records') {
    return 'That range covers more than 10,000 records. Narrow the dates or pick one caller.';
  }
  if (err.status === 429) {
    return 'You have run many narrowed exports in a short time. Please try again shortly.';
  }
  if (err.status === 400 && err.server_reason === 'scope_range_shape') {
    return 'The dates are not valid calendar dates.';
  }
  if (err.status === 400 && err.server_reason === 'scope_range_too_wide') {
    return 'Narrowed exports cover at most 31 days. Shorten the range.';
  }
  return null;
}

/**
 * Plain-language export error. Transport failures (interrupted or timed-out
 * download, unreadable response) read as "try again"; anything else keeps
 * the server's short reason.
 */
export function exportErrorMessage(label: 'Export' | 'Bundle export', err: unknown): string {
  if (err instanceof ApiError) {
    if (err.kind === 'network_error' || err.kind === 'bad_response') {
      return `${label} didn't finish downloading. Try again.`;
    }
    return `${label} failed: ${err.server_reason ?? err.kind}`;
  }
  return `${label} failed.`;
}
